import EditModuleDurationModal from "@/components/modals/learningPath/EditModuleDurationModal"
import { useCan } from "@/utils/can"
import { formatModuleHours } from "@/utils/moduleDuration"
import { useLearningPathWithModulesSuspenseQuery } from "@/api/queries/learningpaths/useLearningPathWithModulesSuspenseQuery"
import EditExternalTeachingMaterialsModal from "@/components/modals/learningPath/EditExternalTeachingMaterialsModal"
import EditModuleExamsModal from "@/components/modals/learningPath/EditModuleExamsModal"
import EditToptaalTeachingMaterialsModal from "@/components/modals/learningPath/EditToptaalTeachingMaterialsModal"
import { checkPermissions } from "@/utils/checkPermissions"
import { formatExams, formatTeachingMaterials } from "@/utils/module"
import { createFileRoute, useParams } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import {
  Button,
  CollapsibleInfoRow,
  hourTypesOptions,
  startLevelMap,
} from "toptaal-shared"

export const Route = createFileRoute(
  "/beheer/leerlijnen/$learningPathId/modules/$moduleId",
)({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "curriculum"),
})

function RouteComponent() {
  const can = useCan()
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false)
  const [
    isExternalTeachingMaterialsModalOpen,
    setIsExternalTeachingMaterialsModalOpen,
  ] = useState(false)

  const [
    isToptaalTeachingMaterialsModalOpen,
    setIsToptaalTeachingMaterialsModalOpen,
  ] = useState(false)

  const [isExamsModalOpen, setIsExamsModalOpen] = useState(false)

  const { learningPathId, moduleId } = useParams({
    from: "/beheer/leerlijnen/$learningPathId/modules/$moduleId",
  })

  const { data: learningPathWithModules } =
    useLearningPathWithModulesSuspenseQuery(Number(learningPathId))

  const module = useMemo(() => {
    return learningPathWithModules.modules.find(
      (m) => m.id === Number(moduleId),
    )
  }, [learningPathWithModules.modules, moduleId])

  if (!module) {
    return (
      <div className="p-4 text-center text-gray-500">Module niet gevonden</div>
    )
  }

  return (
    <div className="space-y-2">
      <h2 className="text-xl font-semibold">Module {module.moduleCode}</h2>
      <CollapsibleInfoRow
        title="Algemeen"
        canEdit={can("update", "curriculum")}
        onEditClick={() => setIsDurationModalOpen(true)}
        values={[
          { label: "Module code", value: module.moduleCode },
          {
            label: "Soort uren",
            value:
              hourTypesOptions.find(
                (option) => option.value === module.hourType,
              )?.label ?? "—",
          },
          {
            label: "Startniveau",
            value: startLevelMap[module.startLevel] ?? module.startLevel,
          },
          {
            label: "Aantal minuten per les",
            value: String(module.lessonDurationInMinutes),
          },
          { label: "Aantal lessen", value: String(module.numberOfLessons) },
          {
            label: "Totaal aantal uren",
            value: formatModuleHours(
              module.numberOfLessons,
              module.lessonDurationInMinutes,
            ),
          },
        ]}
      />
      <CollapsibleInfoRow
        title="Toetsen"
        canEdit={can("update", "curriculum")}
        onEditClick={() => setIsExamsModalOpen(true)}
        values={[
          {
            label: "TopTaal toetsen",
            value: formatExams(module.examsToptaal),
            hideIfEmpty: false,
          },
          {
            label: "Cito toetsen",
            value: formatExams(module.examsCito),
            hideIfEmpty: false,
          },
          {
            label: "VNT2 toetsen",
            value: formatExams(module.examsVnt2),
            hideIfEmpty: false,
          },
        ]}
      />
      <CollapsibleInfoRow
        title="Lesmateriaal"
        values={[
          {
            label: "Lesmateriaal extern",
            value: (
              <div className="space-y-2">
                <p>
                  {formatTeachingMaterials(module.teachingMaterials) ?? "—"}
                </p>
                {can("update", "curriculum") && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setIsExternalTeachingMaterialsModalOpen(true)
                    }
                  >
                    Extern lesmateriaal wijzigen
                  </Button>
                )}
              </div>
            ),
          },
          {
            label: "Lesmateriaal TopTaal",
            value: (
              <div className="space-y-2">
                <p>
                  {formatTeachingMaterials(module.toptaalTeachingMaterials) ??
                    "—"}
                </p>
                {can("update", "curriculum") && (
                  <Button
                    variant="secondary"
                    onClick={() => setIsToptaalTeachingMaterialsModalOpen(true)}
                  >
                    TopTaal lesmateriaal wijzigen
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {isDurationModalOpen && (
        <EditModuleDurationModal
          module={module}
          onClose={() => setIsDurationModalOpen(false)}
        />
      )}

      {isExternalTeachingMaterialsModalOpen && (
        <EditExternalTeachingMaterialsModal
          isOpen={isExternalTeachingMaterialsModalOpen}
          onClose={() => setIsExternalTeachingMaterialsModalOpen(false)}
          module={module}
        />
      )}

      {isToptaalTeachingMaterialsModalOpen && (
        <EditToptaalTeachingMaterialsModal
          isOpen={isToptaalTeachingMaterialsModalOpen}
          onClose={() => setIsToptaalTeachingMaterialsModalOpen(false)}
          module={module}
        />
      )}

      {isExamsModalOpen && (
        <EditModuleExamsModal
          isOpen={isExamsModalOpen}
          onClose={() => setIsExamsModalOpen(false)}
          learningPathId={Number(learningPathId)}
          module={module}
        />
      )}
    </div>
  )
}
